import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { CitadelNotificationService, CitadelErrorLoggingService } from '@citadel/core';
import { RegistrationService } from 'src/app/services/registration-service.service';
import { SignInService } from 'src/app/services/sign-in-service';

@Component({
  selector: 'app-sign-in-otp-entry-step',
  templateUrl: './sign-in-otp-entry-step.component.html',
  styleUrls: ['./sign-in-otp-entry-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignInOtpEntryStepComponent {
  private router = inject(Router);
  private notify = inject(CitadelNotificationService);
  private errorLogging = inject(CitadelErrorLoggingService);
  private signInService = inject(SignInService);
  private registration = inject(RegistrationService);

  private defaultErrorMessage: string = 'An error occurred. Please try again';
  protected otpType = this.signInService.otpType();
  private otpValue: string = '';
  protected resendOtpText: string = 'Didn\'t receive the OTP?';
  protected showPinRequiredMsg: boolean = false;
  protected isSigningIn: boolean = false;
  private isResending: boolean = false;

  protected updateOtp(e: string): void {
    if (e) {
      this.showPinRequiredMsg = false;
    }
    this.otpValue = e;
  }

  protected resendOTP(): void {
    if (this.otpType === 'Email') {
      this.resendOtpEmail();
      return;
    }
    this.signInService.otpSent.set(false);
    this.router.navigate(['/sign-in', 'otp-channel']);
  }

  /** EmailOtp accounts have no other channel: mail the OTP again and stay here. */
  private resendOtpEmail(): void {
    if (this.isResending) {
      return;
    }
    this.isResending = true;
    this.registration.sendOtpEmail(this.signInService.account()?.requestID ?? '').subscribe({
      next: _ => {
        this.isResending = false;
        this.notify.success('OTP sent', "");
      }, error: _ => {
        this.isResending = false;
        this.notify.error(this.defaultErrorMessage, "");
      }
    });
  }

  protected signIn() {
    if (this.isSigningIn) {
      return;
    }

    if (!this.otpValue || this.otpValue.length < 4) {
      if (this.otpValue.length === 0) {
        this.showPinRequiredMsg = true;
      }
      this.notify.error('A 4-digit OTP is required', " ");
    } else {
      this.showPinRequiredMsg = false;
      this.isSigningIn = true;
      const account = this.signInService.account();
      const returnUrl = this.signInService.returnUrl();
      this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: Signing in with returnUrl: ${returnUrl}, userName: ${account?.userName}, authenticationTypeL: ${account?.authenticationTypeL}, requestID: ${account?.requestID}`);

      // A real form post, submitted from the click itself: Safari blocked the earlier sign-in by API call.
      try {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/api/auth/form';

        const fields: { [key: string]: string } = {
          returnUrl,
          userName: account?.userName ?? '',
          authenticationTypeL: String(account?.authenticationTypeL ?? 0),
          requestID: account?.requestID ?? '',
          password: this.signInService.password(),
          otp: String(parseInt(this.otpValue)),
          correlationID: this.errorLogging.correlationID || crypto.randomUUID(),
          clientDateTime: new Date().toISOString(),
          authRedirect: this.signInService.authRedirect(),
        };

        for (const name of Object.keys(fields)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = fields[name];
          form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
      } catch (_error) {
        this.isSigningIn = false;
        this.notify.error(this.defaultErrorMessage, "");
      }
    }
  }

  protected handleKeyUp(e: KeyboardEvent) {
    if (e.keyCode === 13) {
      this.signIn();
    }
  }
}
